/* The case workspace, assembled from five reads into one `CaseDossier`.
 *
 * The API keeps these apart for good reasons — values are corrected one at a
 * time under an ETag, gaps close individually, intake items are evidence and
 * never change — but a reviewer opens one screen and expects one picture. This
 * file is where the five become one, and it does no fetching: the transport
 * gathers, this maps.
 *
 * What comes from where:
 *
 *   detail     the case row, plus the intake items for channels and attachments
 *   values     characteristicValue, with its raw values and provenance inline
 *   conflicts  conflict, with candidates, resolved against those raw values
 *   ambiguous  the open gaps whose class is not simply "missing"
 *   timeline   the case's own stateTransition history
 *
 * One index is required and cannot be derived from any of them: MRI field id →
 * definition. Values, gaps and conflicts all reference an MRI field by id, and
 * every one of the three needs its name to be legible. The transport loads the
 * case's pinned field set once and hands it in.
 */

import type { components } from '@/shared/api/generated/openapi';
import { DASH, formatRelative, formatStamp, formatWhen } from '@/shared/api/http/map/format';
import { readable } from '@/shared/api/http/map/ids';
import type { CaseDossier } from '@/shared/api/transport';
import type {
  CaseConflict,
  CaseDetail,
  Channel,
  ConflictOption,
  EvidenceItem,
  EvidenceKindKey,
  TimelineEntry,
} from '@/shared/domain/case';
import type { MriValue } from '@/shared/domain/mri';
import type { MriDimension, MriFieldAdmin } from '@/shared/domain/mriAdmin';

import type { WireCase } from './cases';

export type WireValue = components['schemas']['CharacteristicValueModel'];
export type WireRawValue = components['schemas']['MriRawValueModel'];
export type WireProvenance = components['schemas']['ProvenanceModel'];
export type WireConflict = components['schemas']['ConflictModel'];
export type WireGap = components['schemas']['GapModel'];
export type WireCaseItem = components['schemas']['CaseItemModel'];
export type WireTransition = components['schemas']['CaseStateTransitionModel'];

/** The five reads the transport makes for one case, before any mapping. */
export interface DossierSources {
  readonly case: WireCase;
  readonly values: readonly WireValue[];
  readonly conflicts: readonly WireConflict[];
  readonly gaps: readonly WireGap[];
  /** Intake items bound to the case. Empty is legitimate — a case opened by hand
      has none — and produces a detail with no channels rather than a failure. */
  readonly items: readonly WireCaseItem[];
}

export interface DossierContext {
  /** MRI field id → its pinned definition. Without it every value is nameless. */
  readonly fields?: ReadonlyMap<string, MriFieldAdmin>;
  readonly now?: Date;
}

/** The MRI field's technical name, which is what the cockpit keys on.
 *
 * `businessName` is the human label and has its own column in the MRI grid; the
 * `name` is what `caseMri()` matches gaps and values against, so substituting one
 * for the other here would break the join rather than improve the wording.
 *
 * An id with no entry falls back to the id. It reads badly, and that is the
 * point: a referenced field absent from the pinned set is a real inconsistency,
 * and printing the id is what lets somebody go and find it. */
function nameOf(
  ref: { readonly id?: string } | null | undefined,
  index: ReadonlyMap<string, MriFieldAdmin> | undefined,
): string {
  const id = ref?.id;
  if (!id) return DASH;
  return index?.get(id)?.name ?? id;
}

function dimensionOf(
  ref: { readonly id?: string } | null | undefined,
  index: ReadonlyMap<string, MriFieldAdmin> | undefined,
): MriDimension {
  return (ref?.id ? index?.get(ref.id)?.mriDimension : undefined) ?? 'What';
}

/* ---------- values ---------- */

/** The one populated slot, as something a reader could quote — or nothing.
 *
 * The API renders a value in the shape its `valueType` implies — a scalar, a
 * `TimePeriod`, or a `ResourceRef` for the seven reference forms — and the grid
 * has one column. `undefined` means this shape produced no label, which is not
 * the same as the slot being empty; `renderValue()` below tells the two apart.
 *
 * Every string leaves through `readable()`. A `ResourceRef` is the reason —
 * `rendered_value()` on the server builds one from a foreign key and resolves no
 * `name`, so `shape['id']` is a uuid every time, and the value column was
 * printing it. But the guard is on all the string paths rather than on that one,
 * because a `value_text` slot holding a uuid is the same unreadable cell
 * arriving by a different route. */
function labelOf(value: unknown): string | undefined {
  if (typeof value === 'string') return readable(value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'object' || value === null) return undefined;

  const shape = value as Record<string, unknown>;

  const from = shape['startDateTime'];
  const to = shape['endDateTime'];
  if (typeof from === 'string' || typeof to === 'string') {
    const start = typeof from === 'string' ? formatStamp(from) : DASH;
    const end = typeof to === 'string' ? formatStamp(to) : 'open';
    return `${start} → ${end}`;
  }

  /* The id last, and only if it survives `readable()`. Some deployments put a
     business key in the slot a reference points at — `SSPEC-2210` is a thing an
     operator quotes in a ticket — and that is worth showing. A uuid is not, and
     this is where the two are told apart. */
  const name = shape['name'];
  const id = shape['id'];
  return (
    (typeof name === 'string' ? readable(name) : undefined) ??
    (typeof id === 'string' ? readable(id) : undefined)
  );
}

/** What the source actually said, for a value whose own shape names nothing.
 *
 * The raw reading is the text the extractor read for this value — "Aurora Health
 * Group", "R. Hoffmann <r.hoffmann@aurora-health.example>" — and for a reference
 * it is the only human form of it anywhere in the payload. It is what the
 * conflicts panel already shows a reviewer for exactly this reason.
 *
 * `rawText` before `normalisedText`, which is the opposite of `candidateValue()`
 * below and deliberate: normalisation exists to make a value comparable, not
 * readable, and it case-folds — a customer rendered as "aurora health group" is
 * legible but wrong on a screen a person signs off from. */
function rawReading(value: WireValue): string | undefined {
  for (const raw of value.rawValue ?? []) {
    const reading = readable(raw.rawText) ?? readable(raw.normalisedText);
    if (reading) return reading;
  }
  return undefined;
}

/** The value as the grid holds it: a label, or a dash — never an identifier.
 *
 * An empty slot is answered before the readings are consulted. A value with no
 * populated slot is a value that says nothing, and a raw reading behind it is
 * text the extractor captured and the system then declined to accept — putting
 * it in the value column would show a reading as though it were the value.
 *
 * The dash a uuid falls to is a real loss and is still the right answer. A
 * reference the server did not resolve, whose readings are gone or were never
 * captured, is a value this screen cannot name; printing its uuid would fill the
 * cell without telling the reader anything they can search a contract by, read
 * aloud, or recognise on the second row.
 *
 * A dash rather than `null`, because the two are different facts here and the
 * screen counts on the difference: `caseMri()` reads a null value as a field
 * that is still open, and the MRI tab's "Still open" filter tests for it. This
 * field is not open. It has an answer that could not be put into words. */
function renderValue(value: WireValue): string {
  if (value.value === null || value.value === undefined) return DASH;
  return labelOf(value.value) ?? rawReading(value) ?? DASH;
}

/** Where the value came from, in the short form the evidence panel keys on.
 *
 * `locator` is exactly this concept on the server — "mail#4 ¶1", "attach:
 * sow-v2.pdf", "snow.u_requested_date" — so it is used verbatim rather than
 * reconstructed. Falling back through the extractor to the authorship keeps the
 * column populated for values that were typed by a person and therefore cite
 * nothing. */
function sourceOf(value: WireValue): string {
  const raw = value.rawValue?.[0];
  const locator = raw?.provenance?.[0]?.locator;
  if (locator) return locator;
  if (raw?.extractedBy) return raw.extractedBy;
  return value.authorship === 'human' ? 'manual entry' : 'analysis';
}

/** Two of the four authorships are a person's; the grid has two columns' worth
    of trust to express and shows the stronger claim. `ai-suggested-human-accepted`
    counts as Human because a person looked at it and said yes. */
function authorOf(value: WireValue): MriValue['author'] {
  return value.authorship === 'human' || value.authorship === 'ai-suggested-human-accepted'
    ? 'Human'
    : 'AI';
}

export function toMriValues(
  values: readonly WireValue[],
  context: DossierContext = {},
): readonly MriValue[] {
  return values
    // A missing value is a gap, not a row: the MRI grid draws mandatory fields
    // from the pinned field set and fills them here, so an empty row would show the
    // field twice — once as filled with nothing, once as open.
    .filter((value) => value.status !== 'missing')
    .map((value) => ({
      group: dimensionOf(value.characteristic, context.fields),
      name: nameOf(value.characteristic, context.fields),
      value: renderValue(value),
      source: sourceOf(value),
      confidence: value.confidence ?? 0,
      author: authorOf(value),
    }));
}

/* ---------- evidence ---------- */

/** The wire's slug for a kind of source → the cockpit's key for it. A slug with
    no entry is not in this table and is carried verbatim instead: the reader is
    better served by `carrier-pigeon` than by a word the cockpit invented. */
const EVIDENCE_KINDS: Readonly<Record<string, EvidenceKindKey>> = {
  'servicenow-ticket': 'servicenowTicket',
  'servicenow-comment': 'servicenowComment',
  email: 'email',
  attachment: 'attachment',
  'human-entry': 'humanEntry',
};

/** What kind of thing the source is, as the cockpit's key where it has one and
 *  as the API's own slug where it does not — and neither where the provenance
 *  row named no kind at all, in which case the locator is the whole heading. */
function kindOf(sourceType: string | undefined): Pick<EvidenceItem, 'kindKey' | 'source'> {
  if (!sourceType) return {};
  const kindKey = EVIDENCE_KINDS[sourceType];
  return kindKey ? { kindKey } : { source: sourceType };
}

/** Every cited source on the case, keyed by the locator the values cite.
 *
 * The excerpt is the span the extractor actually read. Where a provenance row
 * carries none, the raw text stands in — a reviewer asking "where did this come
 * from" is better served by what the source said than by an empty panel.
 *
 * Deliberately per case rather than global: provenance belongs to one case's
 * values, and there is no collection of it across cases. Building one would mean
 * reading every case's values to answer a question about one. */
export function collectEvidence(
  values: readonly WireValue[],
): Readonly<Record<string, EvidenceItem>> {
  const evidence: Record<string, EvidenceItem> = {};
  for (const value of values) {
    for (const raw of value.rawValue ?? []) {
      for (const provenance of raw.provenance ?? []) {
        if (!provenance.locator || evidence[provenance.locator]) continue;
        evidence[provenance.locator] = {
          /* No heading assembled here. The kind is a cockpit word and the
             locator is the server's, and `vocabulary.evidenceTitle()` joins
             them at render — this record is cached, so a heading written here
             would keep the language the case was fetched in. */
          title: null,
          ...kindOf(provenance.sourceType),
          quote: provenance.excerpt ?? raw.rawText,
        };
      }
    }
  }
  return evidence;
}

/* ---------- conflicts ---------- */

function rawValueIndex(values: readonly WireValue[]): Map<string, WireRawValue> {
  const index = new Map<string, WireRawValue>();
  for (const value of values) {
    for (const raw of value.rawValue ?? []) index.set(raw.id, raw);
  }
  return index;
}

/** What a candidate is offering, preferring the normalised form.
 *
 * The raw text is kept beside it and shown when normalisation failed — "the
 * 30th" is not a date the system can act on, and showing it as one would hide
 * exactly the ambiguity the reviewer is being asked to settle. */
function candidateValue(raw: WireRawValue | undefined): string {
  if (!raw) return DASH;
  if (raw.normalisedText) return raw.normalisedText;
  if (raw.normalisedNumber !== null && raw.normalisedNumber !== undefined) {
    return String(raw.normalisedNumber);
  }
  if (raw.normalisedTimestamp) return formatStamp(raw.normalisedTimestamp);
  if (raw.normalisedBoolean !== null && raw.normalisedBoolean !== undefined) {
    return raw.normalisedBoolean ? 'Yes' : 'No';
  }
  return raw.rawText;
}

function toConflictOption(
  candidate: NonNullable<WireConflict['candidate']>[number],
  raws: ReadonlyMap<string, WireRawValue>,
): ConflictOption {
  const raw = candidate.rawValue?.id ? raws.get(candidate.rawValue.id) : undefined;
  const provenance = raw?.provenance?.[0];
  const quote = provenance?.excerpt;
  return {
    src: provenance?.locator ?? candidate.sourceSummary,
    value: candidateValue(raw),
    line1: candidate.sourceSummary,
    line2: raw ? formatStamp(raw.capturedAt) : DASH,
    strength: raw?.sourceStrength ?? 'unknown',
    ...(quote ? { quote: `“${quote}”` } : {}),
  };
}

export function toCaseConflicts(
  conflicts: readonly WireConflict[],
  values: readonly WireValue[],
  context: DossierContext = {},
): readonly CaseConflict[] {
  const raws = rawValueIndex(values);
  return conflicts.map((conflict) => ({
    name: nameOf(conflict.characteristic, context.fields),
    dimension: dimensionOf(conflict.characteristic, context.fields),
    options: (conflict.candidate ?? []).map((candidate) =>
      toConflictOption(candidate, raws),
    ),
    evidence: conflict.evidenceSummary,
  }));
}

/* ---------- gaps ---------- */

/** Open gaps whose reason is something other than "nobody said".
 *
 * The Gaps tab draws two lists from one collection. A `missing` gap is a field
 * with no answer and its remedy is to ask; the other four classes — invalid,
 * ambiguous, conflicting, unverifiable — are fields that *have* an answer the
 * system could not use, and their remedy is for a person to read the reason.
 * Keyed by characteristic name because that is what the grid joins on.
 */
export function toAmbiguous(
  gaps: readonly WireGap[],
  context: DossierContext = {},
): Readonly<Record<string, string>> {
  const ambiguous: Record<string, string> = {};
  for (const gap of gaps) {
    if (gap.status !== 'open' || gap.gapClass === 'missing') continue;
    ambiguous[nameOf(gap.characteristic, context.fields)] = gap.reason;
  }
  return ambiguous;
}

/** Every open gap's characteristic, in the order the API returned them.
 *
 * All open gaps, not only the `missing` ones: `CaseDetail.missing` answers "what
 * is standing between this case and sign-off", and a field whose value is
 * ambiguous stands there just as squarely as a field with no value at all. The
 * API already orders blocking-first, so the first names a reader sees are the
 * ones that actually hold the case. */
function toMissing(
  gaps: readonly WireGap[],
  context: DossierContext = {},
): readonly string[] {
  const names: string[] = [];
  for (const gap of gaps) {
    if (gap.status !== 'open') continue;
    const name = nameOf(gap.characteristic, context.fields);
    if (!names.includes(name)) names.push(name);
  }
  return names;
}

/* ---------- detail ---------- */

/** Which of the two channels an intake item arrived on.
 *
 * A ServiceNow comment is the ServiceNow channel — the reader is being told
 * where to go and look, and the answer for both is the ticket. A manual entry is
 * neither, and contributes no channel rather than being filed under one it did
 * not come through. */
function channelOf(item: WireCaseItem): Channel | null {
  if (item.sourceType === 'servicenow-ticket' || item.sourceType === 'servicenow-comment') {
    return 'SNOW';
  }
  return item.sourceType === 'email' ? 'MAIL' : null;
}

/** The ticket people quote, preferring what the case itself records.
 *
 * `troubleTicketRef` is the case's own answer and survives the intake items
 * being filtered or archived. The first ServiceNow item's `sourceId` is the
 * fallback, and `null` — meaning the case exists on email alone — is a real
 * answer the header renders as such. */
function ticketOf(wire: WireCase, items: readonly WireCaseItem[]): string | null {
  if (wire.troubleTicketRef) return wire.troubleTicketRef;
  const ticket = items.find((item) => item.sourceType === 'servicenow-ticket');
  return ticket?.sourceId ?? null;
}

export function toCaseDetail(
  wire: WireCase,
  items: readonly WireCaseItem[],
  gaps: readonly WireGap[],
  context: DossierContext = {},
): CaseDetail {
  const now = context.now ?? new Date();
  const channels: Channel[] = [];
  let attachments = 0;

  for (const item of items) {
    const channel = channelOf(item);
    if (channel && !channels.includes(channel)) channels.push(channel);
    // `attachmentCount` is only populated on the list read; the single-item read
    // sends the attachments themselves. Counting whichever arrived keeps the
    // number right under both, and a missing count contributes nothing rather
    // than a zero that would read as "checked, none".
    attachments += item.attachmentCount ?? item.attachment?.length ?? 0;
  }

  return {
    created: formatStamp(wire.createdAt),
    activity: formatRelative(wire.updatedAt, now),
    ticket: ticketOf(wire, items),
    channels,
    attachments,
    missing: toMissing(gaps, context),
  };
}

/* ---------- timeline ---------- */

const ACTORS: Readonly<Record<string, TimelineEntry['actor']>> = {
  human: 'Human',
  ai: 'AI',
  system: 'System',
};

/** State transitions → the timeline, newest first.
 *
 * `reason` is the event, not the state pair. Every transition carries one and
 * the service requires it on write, so the timeline reads as "asked the
 * requester for the delivery site" rather than as
 * "CaseOpen → ClarificationNeeded", which the reader would have to translate.
 *
 * Sorted on `caseVersionAfter` rather than on `occurredAt`: the version is
 * unique per case and strictly increasing, so two moves committed inside the
 * same transaction — which share a timestamp — still read in the order they
 * happened. */
export function toTimeline(
  transitions: readonly WireTransition[],
  now: Date,
): readonly TimelineEntry[] {
  return [...transitions]
    .sort((a, b) => b.caseVersionAfter - a.caseVersionAfter)
    .map((transition) => ({
      actor: ACTORS[transition.actorType] ?? 'System',
      what: transition.reason,
      by: transition.actorId,
      when: formatWhen(transition.occurredAt, now),
    }));
}

/* ---------- the assembly ---------- */

/**
 * The five reads → the one dossier the workspace renders.
 *
 * One `now` for the whole page. The header says "2m ago" and the timeline says
 * "Today · 09:42"; both are measured from the same instant so they cannot
 * disagree about when the last thing happened.
 *
 * Every part degrades on its own. A case whose intake items failed to load still
 * shows its values, its gaps and its history — with no channels and no
 * attachment count, which is visibly nothing rather than quietly wrong.
 */
export function toDossier(
  sources: DossierSources,
  context: DossierContext = {},
): CaseDossier {
  const now = context.now ?? new Date();
  const shared: DossierContext = { ...context, now };

  return {
    detail: toCaseDetail(sources.case, sources.items, sources.gaps, shared),
    conflicts: toCaseConflicts(sources.conflicts, sources.values, shared),
    ambiguous: toAmbiguous(sources.gaps, shared),
    values: toMriValues(sources.values, shared),
    mriFields: [...(shared.fields?.values() ?? [])],
    timeline: toTimeline(sources.case.stateTransition ?? [], now),
  };
}
